import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import CarouselContainer from './CarouselContainer';
import RoomCard from './RoomCard';
import Loading from './Loading';
import { parse } from 'csv-parse/browser/esm/sync';
import Constants from '../constants/Constants';

const PageContainer = styled.div`
  text-align: center;
  padding: 20px;
  background: #D1CDC8;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Title = styled.h1`
  font-family: 'Roboto', sans-serif;
  font-size: 3rem;
  color: #333;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    width: 60%;
    height: 2px;
    background: linear-gradient(to right, #D1CDC8, #4A4A4A);
    transform: translateX(-50%);
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 1.5rem;
  color: #666;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 20px;
  }
`;

const ActionButton = styled.a`
  display: inline-block;
  background-color: #61916e;
  color: white;
  padding: 15px 25px;
  margin: 0px 0;
  text-align: center;
  text-decoration: none;
  font-size: 18px;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(45deg);
    animation: shine 1s infinite;
  }

  &:hover {
    background-color: #45a049;
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @keyframes shine {
    0% {
      top: -50%;
      left: -50%;
    }
    100% {
      top: 150%;
      left: 150%;
    }
  }
`;

const HomeLink = styled(Link)`
  display: inline-block;
  margin-top: 20px;
  font-size: 16px;
  color: #61916e;
  text-decoration: none;
  border: 1px solid #61916e;
  padding: 10px 20px;
  border-radius: 5px;
  transition: all 0.3s ease;

  &:hover {
    background-color: #61916e;
    color: white;
  }
`;

const Description = styled.pre`
  font-family: 'Roboto', sans-serif;
  font-size: 1.0rem;
  color: #111;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto 10px;
  text-align: justify;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: ${props => props.$expanded ? 'none' : '100px'};
  overflow: hidden;
  position: relative;

  ${props => !props.$expanded && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 50px;
      background: linear-gradient(to bottom, transparent, #D1CDC8);
      pointer-events: none;
    }
  `}

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 15px;
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: #61916e;
  cursor: pointer;
  font-size: 1rem;
  margin-bottom: 20px;
  text-decoration: underline;

  &:hover {
    color: #45a049;
  }
`;


function RoomHomePage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [localCaseData, setLocalCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [caseData, setCaseData] = useState(undefined);
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const showAll = searchParams.has('show_all');

  const handlers = useSwipeable({
    onSwipedRight: () => {
      navigate('/room');
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  useEffect(() => {
    const fetchLocalCaseData = async () => {
      try {

        // get new server configuration data with upload images support
        let data = undefined;
        try {
          const response = await fetch(`${Constants.get.SERVER_URL}/cases-data/${caseId}`);
          if (!response.ok) {
            throw new Error('Failed to fetch case data');
          }
          data = await response.json();

          // Prepend server url for every url in data, recursively
          function prependServerUrl(obj) {
            if (!obj || typeof obj !== 'object') return obj;
            for (const key in obj) {
              if (!obj.hasOwnProperty(key)) continue;
              const val = obj[key];
              if (typeof val === 'string' && (key.toLowerCase().includes('url') || (val.match(/^\/[^/]/) && !val.startsWith('//')))) {
                // If value looks like a url string starting with '/'
                if (val.startsWith('/') && !val.startsWith('//') && !val.startsWith(`${Constants.get.SERVER_URL}`)) {
                  obj[key] = `${Constants.get.SERVER_URL}${val}`;
                }
              } else if (typeof val === 'object' && val !== null) {
                prependServerUrl(val);
              } else if (Array.isArray(val)) {
                val.forEach(item => prependServerUrl(item));
              }
            }
            return obj;
          }
          prependServerUrl(data);

          console.log("api data:", data);
        } catch (error) {
          console.log("error fetching local data from api:", error);
        }

        if ( data === undefined ) {
        // get legay hard coded data from local file
          try {
            const response = await fetch(`/room_config/${caseId}.json`);
            if (!response.ok) {
              throw new Error('Room not found');
            }
            data = await response.json();
            console.log("local data:", data);
          } catch (error) {
            console.error('Error fetching room config:', error);
            // throw error; // Re-throw to be caught by outer catch block
          }
        }

        if (data) {
          setLocalCaseData(data);
        } else {
          setLocalCaseData(undefined);
        }

        const caseDataResponse = await fetchCaseData();
        if (caseDataResponse) {
           console.log(caseDataResponse);
            setCaseData(caseDataResponse);
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLocalCaseData();
  }, [caseId]);

  const toggleDescription = () => {
    setExpanded(!expanded);
  };

  const fetchCaseData = async () => {
    try {
      const response = await fetch(`${Constants.get.SERVER_URL}/case/${caseId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch case data');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching case data:', error);
      return null;
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!localCaseData) {
    return <div>Room not found</div>;
  }

  // Sort rooms to put available ones first
  const sortedRooms = [...localCaseData.rooms]
  // console.log(caseData);

  return (
    <PageContainer {...handlers}>
      <div>
        <Title>{caseData.property_name}</Title>
      </div>
      <div>
        <Subtitle>{caseData.district}</Subtitle>
        <Description $expanded={expanded}>{caseData.Description}</Description>
        {caseData.Description && (
          <ReadMoreButton onClick={toggleDescription}>
            {expanded ? 'Read Less' : 'Read More'}
          </ReadMoreButton>
        )}
      </div>

      <ActionButton 
        href='https://forms.gle/27Sbo9JF8rhmFfF9A'
        target="_blank" 
        rel="noopener noreferrer"
      >
        點擊填寫租客表單
      </ActionButton>
      
      <CarouselContainer 
        images={localCaseData.images}
        width="90%" 
        $maxWidth="1200px"
        $aspectRatio="60%" 
        padding="10px"
        $borderRadius="12px"
        $objectFit="cover"
        sliderSettings={{
          dots: true,
          infinite: true,
          speed: 500,
          slidesToShow: 3,
          slidesToScroll: 1,
          autoplay: true,
          autoplaySpeed: 3000,
          responsive: [
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 2,
                slidesToScroll: 1,
              }
            },
            {
              breakpoint: 600,
              settings: {
                slidesToShow: 1,
                slidesToScroll: 1
              }
            }
          ]
        }}
      />

      {sortedRooms.map((room, index) => {
        const caseDataRoom = caseData.rooms.find(caseRoom => caseRoom.room_number === room.roomId);

        let status = false;
        if (caseDataRoom !== undefined && caseDataRoom.vacant_for_rent !== '入住中') {
          status = true;
        }
        // Only show if showAll is true or status is true
        if (!showAll && !status) {
          return null;
        }

        let newRentStartDate = undefined;
        if (caseDataRoom && caseDataRoom.vacant_for_rent) {

          const dateRegex = /^\d{4}\/([1-9]|0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1]|[1-9])\/?\d?$/;
          if (dateRegex.test(caseDataRoom.vacant_for_rent)) {
            const date = new Date(caseDataRoom.vacant_for_rent);
            date.setDate(date.getDate() + 1);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            newRentStartDate = `${year}/${month}/${day}`;
          }
        }

        console.log("caseDataRoom:", caseDataRoom);
        return (
          <RoomCard 
            key={index}
            roomName={room.title}
            roomTitle={caseDataRoom.room_title}
            newRentStartDate={newRentStartDate}
            status={status}
            images={room.images}
            baseRent={caseDataRoom ? caseDataRoom.base_rent : 0}
            youtubeUrl={room ? room.youtube_url : null}
          />
        );
      })}

      <HomeLink to="/room">Back to Home</HomeLink>
    </PageContainer>
  );
}

export default RoomHomePage;
